import React from "react";
import { Grid, Typography, Box, Alert, Chip } from "@mui/material";
import type { Plan, EntitlementConfiguration } from "client/api-v2";
import { Fieldset } from "components/form/Fieldset";
import { ENTITLEMENT_FIELD_ORDER } from "../../constants/entitlement-field-order";
import {
  getAllPlanInclusions,
  getInclusionValue,
  formatInclusionName,
  getPlanInclusionType,
  formatEntitlementValue,
} from "../../utils/plan-utils";

interface EntitlementViewerProps {
  plan?: Plan;
  entitlementOverrides?: EntitlementConfiguration;
}

interface LimitValue {
  isUnlimited?: boolean;
  limit?: number | null;
  overrun?: number | null;
  countDisabledUnits?: boolean;
}

interface ReadOnlyEntitlementValueProps {
  name: string;
  value: unknown;
  fieldType: "int" | "boolean" | "intOrUnlimited" | "intWithOverrun";
  baseValue?: string;
}

function isLimitValue(value: unknown): value is LimitValue {
  return (
    typeof value === "object" &&
    value !== null &&
    ("isUnlimited" in value || "limit" in value || "overrun" in value || "countDisabledUnits" in value)
  );
}

function getNumericValue(value: unknown): number | null {
  if (typeof value === "number") return value;
  if (isLimitValue(value)) {
    if (value.isUnlimited === true) return Infinity;
    return value.limit ?? null;
  }
  return null;
}

function isPlanValueHigher(planValue: unknown, overrideValue: unknown): boolean {
  const planNum = getNumericValue(planValue);
  const overrideNum = getNumericValue(overrideValue);
  if (planNum === null || overrideNum === null) return false;
  return planNum > overrideNum;
}

function ReadOnlyEntitlementValue({ name, value, fieldType, baseValue }: ReadOnlyEntitlementValueProps) {
  const renderValue = () => {
    if (value === null || value === undefined) {
      return (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          Not configured
        </Typography>
      );
    }

    switch (fieldType) {
      case "boolean":
        return (
          <Chip
            label={value ? "Enabled" : "Disabled"}
            color={value ? "success" : "default"}
            size="small"
            variant="outlined"
          />
        );

      case "int":
        return (
          <Typography variant="body1" component="span">
            {typeof value === "number" ? value.toLocaleString() : String(value)}
          </Typography>
        );

      case "intOrUnlimited":
      case "intWithOverrun": {
        if (isLimitValue(value)) {
          if (value.isUnlimited === true) {
            return <Chip label="Unlimited" color="primary" size="small" variant="outlined" />;
          }

          const limitText = value.limit !== null && value.limit !== undefined ? value.limit.toLocaleString() : "0";
          const overrunText =
            value.overrun !== null && value.overrun !== undefined ? ` (+${value.overrun} overrun)` : "";
          const disabledUnitsText = value.countDisabledUnits === true ? " (counts disabled units)" : "";

          return (
            <Typography variant="body1" component="span">
              {limitText}
              {overrunText}
              {disabledUnitsText}
            </Typography>
          );
        }

        // Fallback for simple number values
        return (
          <Typography variant="body1" component="span">
            {String(value)}
          </Typography>
        );
      }

      default:
        return (
          <Typography variant="body1" component="span">
            {String(value)}
          </Typography>
        );
    }
  };

  return (
    <Fieldset title={name}>
      <Box sx={{ minHeight: 40, display: "flex", flexDirection: "column", justifyContent: "center", gap: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>{renderValue()}</Box>
        {baseValue !== undefined && (
          <Chip
            label={`Plan: ${baseValue}`}
            color="warning"
            size="small"
            variant="outlined"
            sx={{ width: "fit-content" }}
          />
        )}
      </Box>
    </Fieldset>
  );
}

export function EntitlementViewer({ plan, entitlementOverrides }: EntitlementViewerProps) {
  if (!plan) {
    return (
      <Box sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
        <Typography variant="body2">No plan selected</Typography>
      </Box>
    );
  }

  const planInclusionKeys = getAllPlanInclusions(plan);
  const overrideKeys = entitlementOverrides
    ? Object.entries(entitlementOverrides)
        .filter(([, value]) => value !== null && value !== undefined)
        .map(([key]) => key)
    : [];

  const allKeys = Array.from(new Set([...planInclusionKeys, ...overrideKeys]));

  if (allKeys.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">No entitlements are defined for this plan.</Alert>
      </Box>
    );
  }

  const fieldOrder: readonly string[] = ENTITLEMENT_FIELD_ORDER;
  const sortedKeys = [...allKeys].sort((a, b) => {
    const indexA = fieldOrder.indexOf(a);
    const indexB = fieldOrder.indexOf(b);

    if (indexA === -1 && indexB === -1) return a.localeCompare(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Entitlements
      </Typography>
      <Grid container spacing={2}>
        {sortedKeys.map((key) => {
          const planValue = getInclusionValue(plan.planInclusions, key);
          const overrideValue = getInclusionValue(entitlementOverrides, key);
          const hasOverride = overrideValue !== undefined && overrideValue !== null;
          const effectiveValue = hasOverride ? overrideValue : planValue;
          const isOverrideDifferent =
            hasOverride && formatEntitlementValue(overrideValue) !== formatEntitlementValue(planValue);

          const fieldType = getPlanInclusionType(plan, key, effectiveValue) || "int";

          const displayName = formatInclusionName(key);

          const baseValue =
            isOverrideDifferent && !isPlanValueHigher(planValue, overrideValue)
              ? formatEntitlementValue(planValue)
              : undefined;

          return (
            <Grid
              key={key}
              size={{
                xs: 12,
                sm: 3,
              }}
            >
              <ReadOnlyEntitlementValue
                name={displayName}
                value={effectiveValue}
                fieldType={fieldType}
                baseValue={baseValue}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
